import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button, FormField, Input, Select, Textarea, useToast } from "@/ui";
import { ApiError, ERROR_CODES, requestLabAccess, type LabAccessRequest } from "@/api";
import { AuthShell } from "@/auth/AuthShell";
import { AuthAlert } from "@/auth/AuthAlert";
import { TurnstileWidget } from "@/auth/TurnstileWidget";
import { CheckBigIcon, ChevronBackIcon, MailBigIcon } from "@/auth/icons";
import { WILAYAS } from "@/auth/wilayas";
import { useT } from "./i18n";

/**
 * DemandeAccesPage — the LAB self-signup ("Nouveau laboratoire ? Demander un accès — validé par
 * Pharmakon sous 48 h"). Posts the request via the typed apiClient method `requestLabAccess`
 * (POST /api/v1/public/onboarding/lab-request).
 *
 * NOTE: that onboarding endpoint is built in a LATER admin phase and may be absent today. The call
 * is made FOR REAL; we never fake a silent success. Three terminal states:
 *   • genuine success            → "Demande reçue" (server acknowledged).
 *   • endpoint absent (404 /
 *     NOT_FOUND / NOT_IMPLEMENTED) → optimistic "pending" fallback: the request is acknowledged as
 *                                    received-but-not-yet-routed, with a manual contact path.
 *   • any other failure          → an inline error the user can retry from (form preserved).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  labName: string;
  ownerName: string;
  email: string;
  phone: string;
  wilaya: string;
  commune: string;
  message: string;
}

type Errors = Partial<Record<keyof FormState, string>>;

const EMPTY: FormState = {
  labName: "",
  ownerName: "",
  email: "",
  phone: "",
  wilaya: "",
  commune: "",
  message: "",
};

export function DemandeAccesPage() {
  const { t } = useT();
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The Turnstile widget token (only set when VITE_TURNSTILE_SITE_KEY is configured); the built-in PoW
  // path needs no token (requestLabAccess solves it transparently).
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  // "ok" = server acknowledged · "pending" = endpoint absent, optimistic-received fallback.
  const [outcome, setOutcome] = useState<"ok" | "pending" | null>(null);

  const set = (k: keyof FormState, v: string) => {
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = (): boolean => {
    const next: Errors = {};
    if (!form.labName.trim()) next.labName = t("auth.demande.required");
    if (!form.ownerName.trim()) next.ownerName = t("auth.demande.required");
    if (!form.email.trim()) next.email = t("auth.demande.required");
    else if (!EMAIL_RE.test(form.email.trim())) next.email = t("auth.demande.email.invalid");
    if (!form.wilaya) next.wilaya = t("auth.demande.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!validate()) return;

    const payload: LabAccessRequest = {
      labName: form.labName.trim(),
      ownerName: form.ownerName.trim(),
      email: form.email.trim(),
      wilaya: form.wilaya,
      ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
      ...(form.commune.trim() ? { commune: form.commune.trim() } : {}),
      ...(form.message.trim() ? { message: form.message.trim() } : {}),
    };

    setSubmitting(true);
    try {
      await requestLabAccess(payload, captchaToken);
      setOutcome("ok");
    } catch (err) {
      if (err instanceof ApiError && isEndpointAbsent(err)) {
        // The onboarding service isn't live yet — acknowledge optimistically (NOT a fake success)
        // and give a manual fallback path. The user is told plainly it's pending registration.
        setOutcome("pending");
      } else if (err instanceof ApiError && err.httpStatus === 429) {
        const m = t("auth.demande.ratelimited");
        setError(m);
        toast.error(m);
      } else {
        const m = t("auth.demande.error");
        setError(m);
        toast.error(m);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (outcome) {
    const isOk = outcome === "ok";
    return (
      <AuthShell>
        <div className="nv-auth__confirm">
          <span className={`nv-auth__confirm-badge ${isOk ? "green" : "amber"}`} aria-hidden>
            {isOk ? <CheckBigIcon /> : <MailBigIcon />}
          </span>
          <h1 className="nv-auth__h">
            {isOk ? t("auth.demande.done.title") : t("auth.demande.pending.title")}
          </h1>
          <p className="nv-auth__confirm-text" role="status">
            {isOk ? t("auth.demande.done") : t("auth.demande.pending")}
          </p>
        </div>
        <Link to="/login" className="nv-auth__back">
          <ChevronBackIcon />
          <span>{t("auth.demande.back")}</span>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="nv-auth__h">{t("auth.demande.title")}</h1>
      <p className="nv-auth__sub">{t("auth.demande.subtitle")}</p>

      {error && <AuthAlert tone="red">{error}</AuthAlert>}

      <form onSubmit={onSubmit} noValidate style={{ marginBlockStart: "var(--nv-s-4)" }}>
        <div className="nv-auth__grid2">
          <FormField
            label={t("auth.demande.lab")}
            required
            className="nv-auth__col-2"
            {...(errors.labName ? { error: errors.labName } : {})}
          >
            {(p) => (
              <Input
                {...p}
                placeholder={t("auth.demande.lab.placeholder")}
                value={form.labName}
                onChange={(e) => set("labName", e.target.value)}
                autoComplete="organization"
              />
            )}
          </FormField>

          <FormField
            label={t("auth.demande.owner")}
            required
            {...(errors.ownerName ? { error: errors.ownerName } : {})}
          >
            {(p) => (
              <Input
                {...p}
                placeholder={t("auth.demande.owner.placeholder")}
                value={form.ownerName}
                onChange={(e) => set("ownerName", e.target.value)}
                autoComplete="name"
              />
            )}
          </FormField>

          <FormField
            label={t("auth.demande.phone")}
            {...(errors.phone ? { error: errors.phone } : {})}
          >
            {(p) => (
              <Input
                {...p}
                type="tel"
                inputMode="tel"
                placeholder={t("auth.demande.phone.placeholder")}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                autoComplete="tel"
              />
            )}
          </FormField>

          <FormField
            label={t("auth.demande.email")}
            required
            className="nv-auth__col-2"
            {...(errors.email ? { error: errors.email } : {})}
          >
            {(p) => (
              <Input
                {...p}
                type="email"
                inputMode="email"
                placeholder={t("auth.demande.email.placeholder")}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                autoComplete="email"
              />
            )}
          </FormField>

          <FormField
            label={t("auth.demande.wilaya")}
            required
            {...(errors.wilaya ? { error: errors.wilaya } : {})}
          >
            {(p) => (
              <Select
                {...p}
                placeholder={t("auth.demande.wilaya.placeholder")}
                options={WILAYAS}
                value={form.wilaya}
                onChange={(e) => set("wilaya", e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("auth.demande.commune")}
            {...(errors.commune ? { error: errors.commune } : {})}
          >
            {(p) => (
              <Input
                {...p}
                placeholder={t("auth.demande.commune.placeholder")}
                value={form.commune}
                onChange={(e) => set("commune", e.target.value)}
                autoComplete="address-level2"
              />
            )}
          </FormField>

          <FormField label={t("auth.demande.message")} className="nv-auth__col-2">
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                placeholder={t("auth.demande.message.placeholder")}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
            )}
          </FormField>
        </div>

        <TurnstileWidget onToken={setCaptchaToken} />

        <Button
          type="submit"
          block
          loading={submitting}
          style={{ marginBlockStart: "var(--nv-s-4)" }}
        >
          {submitting ? t("auth.demande.submitting") : t("auth.demande.submit")}
        </Button>
      </form>

      <Link to="/login" className="nv-auth__back">
        <ChevronBackIcon />
        <span>{t("auth.demande.back")}</span>
      </Link>
    </AuthShell>
  );
}

/** The onboarding endpoint isn't built yet → treat a 404 / NOT_FOUND / NOT_IMPLEMENTED as "absent". */
function isEndpointAbsent(err: ApiError): boolean {
  return (
    err.httpStatus === 404 ||
    err.code === ERROR_CODES.NOT_FOUND ||
    err.code === ERROR_CODES.NOT_IMPLEMENTED
  );
}
